// services/integration.service.js
import connectionsClient, { handleApiError } from '../api/connectionsClient';

/**
 * Servicio para gestionar integraciones de múltiples plataformas
 * Base URL: https://conn-dev.zenitailab.com
 */
class IntegrationService {
  /**
   * Listar todas las integraciones para una cuenta
   * GET /integrations/account/{account_id}
   * 
   * @param {string} accountId - ID de la cuenta
   * @param {boolean} onlyActive - Filtrar solo integraciones activas
   * @returns {Promise<{success: boolean, data?: {meta: [], shopify: [], google: []}, error?: any}>}
   */
  async listAllIntegrations(accountId, onlyActive = true) {
    try {
      if (!accountId) {
        return {
          success: false,
          error: { message: 'Account ID is required' }
        };
      }

      const params = new URLSearchParams();
      if (onlyActive) params.append('only_active', 'true');

      const response = await connectionsClient.get(
        `/integrations/account/${accountId}?${params.toString()}`
      );
      
      // Manejar respuesta con estructura Lambda: { statusCode: 200, body: {...} }
      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      return {
        success: true,
        data: {
          meta: data.integrations?.meta || [],
          shopify: data.integrations?.shopify || [],
          google: data.integrations?.google || []
        }
      };
    } catch (error) {
      console.error('Error listing integrations:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener detalles de una integración específica
   * 
   * @param {string} integrationId - UUID de la integración
   * @param {string} type - Tipo de integración: 'meta', 'shopify', 'google'
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async getIntegrationDetails(integrationId, type) {
    try {
      if (!integrationId || !type) {
        return {
          success: false,
          error: { message: 'Integration ID and type are required' }
        };
      }

      let endpoint;
      switch (type.toLowerCase()) {
        case 'meta':
          endpoint = `/meta/integrations/${integrationId}`;
          break;
        case 'shopify':
          endpoint = `/shopify/integrations/${integrationId}`;
          break;
        case 'google':
          endpoint = `/google/integrations/${integrationId}`;
          break;
        default:
          return {
            success: false,
            error: { message: `Unknown integration type: ${type}` }
          };
      }

      const response = await connectionsClient.get(endpoint);
      
      // Manejar respuesta con estructura Lambda
      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      // Extraer la integración de la respuesta
      const integration = data.data?.integration || data.integration || data;

      return {
        success: true,
        data: integration
      };
    } catch (error) {
      console.error('Error getting integration details:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar integraciones de Meta
   * Wrapper para compatibilidad con código existente
   */
  async listMetaIntegrations(accountId) {
    const result = await this.listAllIntegrations(accountId);
    if (result.success) {
      return {
        success: true,
        data: result.data.meta
      };
    }
    return result;
  }

  /**
   * Listar integraciones de Shopify
   * Wrapper para compatibilidad con código existente
   */
  async listShopifyIntegrations(accountId) {
    const result = await this.listAllIntegrations(accountId);
    if (result.success) {
      return {
        success: true,
        data: result.data.shopify
      };
    }
    return result;
  }

  /**
   * Listar integraciones de Google
   * Wrapper para compatibilidad con código existente
   */
  async listGoogleIntegrations(accountId) {
    const result = await this.listAllIntegrations(accountId);
    if (result.success) {
      return {
        success: true,
        data: result.data.google
      };
    }
    return result;
  }
}

export default new IntegrationService();
